import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://litbuyspreadsheets.pics';

const safetySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Safety & Disclaimer — Litbuy Spreadsheet',
  url: `${siteUrl}/safety`,
  description: 'Important safety information, disclaimers, privacy practices, and data security guidance for litbuyspreadsheets.pics. Learn about how we protect your information.',
};

export default function SafetyPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <title>Safety &amp; Disclaimer — Litbuy Spreadsheet | litbuyspreadsheets.pics</title>
      <meta name="description" content="Important safety information, disclaimers, privacy practices, and data security guidance for litbuyspreadsheets.pics. Learn how to protect your data when using spreadsheet tools." />
      <link rel="canonical" href={`${siteUrl}/safety`} />
      <meta property="og:title" content="Safety &amp; Disclaimer — Litbuy Spreadsheet | litbuyspreadsheets.pics" />
      <meta property="og:description" content="Important safety information, disclaimers, privacy practices, and data security guidance for litbuyspreadsheets.pics." />
      <meta property="og:url" content={`${siteUrl}/safety`} />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Safety &amp; Disclaimer — Litbuy Spreadsheet | litbuyspreadsheets.pics" />
      <meta name="twitter:description" content="Important safety information, disclaimers, privacy practices, and data security guidance." />
      <JsonLd data={safetySchema} />
      <Navbar />
      <main className="pt-24 pb-16 px-6 md:px-10 lg:px-16">
        <article className="max-w-3xl mx-auto">
          <header className="mb-10">
            <span className="inline-block px-3 py-1 rounded-full border border-accent-200 text-xs font-medium text-accent-600 tracking-wide mb-4">
              Trust &amp; Safety
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
              Safety &amp; Disclaimer
            </h1>
            <p className="text-sm md:text-base text-foreground-500 leading-relaxed">
              Important safety information, disclaimers, and privacy practices for litbuyspreadsheets.pics.
            </p>
          </header>

          <div className="space-y-6 text-sm md:text-base text-foreground-700 leading-relaxed">
            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">Disclaimer</h2>
              <p>
                This website provides educational information about Litbuy Spreadsheet. We are an independent resource and are <strong className="text-foreground-900">not officially affiliated with Litbuy</strong>. All product names, logos, and brands mentioned on this website are property of their respective owners.
              </p>
              <p className="mt-3">
                Users should <strong className="text-foreground-900">always verify information</strong> before making business decisions. While we strive for accuracy and regularly update our content, we cannot guarantee that all information on this site is current, complete, or error-free at all times. Litbuy Spreadsheet features, pricing, and policies may change without notice.
              </p>
            </div>

            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">Privacy Notice</h2>
              <p>
                Your privacy matters to us. This website <strong className="text-foreground-900">does not collect personal information</strong> beyond what is voluntarily provided through our newsletter subscription form. We do not track browsing behavior, use cookies for advertising purposes, or sell user data to third parties.
              </p>
              <p className="mt-3">
                Our newsletter subscription collects only your email address. You can unsubscribe at any time. We do not share subscriber information with any third parties.
              </p>
            </div>

            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">Data Security Statement</h2>
              <p>
                We recommend that all spreadsheet users follow these security best practices:
              </p>
              <ul className="mt-3 space-y-2">
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  Regularly export and back up your data to a secure offline location.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  Enable multi-factor authentication on all accounts that support it.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  Use strong, unique passwords for each platform you use.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  Keep your software and spreadsheet tools updated to the latest versions.
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 text-primary-500 text-xs">
                    <i className="ri-checkbox-circle-line"></i>
                  </span>
                  Review privacy settings and access permissions regularly.
                </li>
              </ul>
            </div>

            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">External Links</h2>
              <p>
                This website contains links to external websites, including our main website at kaspreadsheet.com. We are not responsible for the content, privacy practices, or security of third-party websites. External links may lead to websites with their own privacy policies and terms of service.
              </p>
              <p className="mt-3">
                When you click external links, you are subject to the destination website policies. We encourage you to review the privacy policies and terms of any website you visit.
              </p>
            </div>

            <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200/60">
              <h2 className="text-lg font-heading font-bold text-foreground-900 mb-3">Affiliate Disclosure</h2>
              <p>
                Some links on this website may be affiliate links. This means we may earn a small commission if you make a purchase through these links, at no additional cost to you. Our content and recommendations are based on research and honest assessment, and we only recommend tools and services we believe provide genuine value.
              </p>
            </div>

            <div className="mt-6 flex items-center gap-4 text-sm">
              <a href="/" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 border-b border-transparent hover:border-primary-300 pb-0.5 whitespace-nowrap">
                &larr; Back to Home
              </a>
              <a href="/faq" className="text-foreground-600 hover:text-primary-500 transition-colors duration-200 border-b border-transparent hover:border-primary-300 pb-0.5 whitespace-nowrap">
                FAQ
              </a>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}